import React, { useState, useMemo, useEffect } from "react";
import Header from "../../../components/Header/Header";
import CommonPieChart from "../../../components/common/Chart/CommonPieChart";
import Profile from "./Profile";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetPerformanceDetailsQuery } from "../../../services/apiSlice";
import LoadingCommon from "../../../components/common/LoadingStatus/LoadingCommon";
import { MetricCardRetention } from "./MetricCardRetention";
import { useTranslation } from "react-i18next";
import images from "../../../images";

const PerformaceAgent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dataChartProps } = location.state || {};

  const [state, setState] = useState({
    activeTab: "profile",
    isMenuOpen: false,
    page: 1,
    limit: 10,
    hasMore: true,
    isTabSwitching: false,
  });

  const [lastValidOption, setLastValidOption] = useState(null);

  const {
    data: performanceDetailsData,
    isLoading: isLoadingPerformanceDetailsData,
    isFetching: isFetchingPerformanceDetailsData,
  } = useGetPerformanceDetailsQuery(
    {
      location_id: dataChartProps?.locationId || "",
      segmantation_id: dataChartProps?.segmentId || "",
      option_id: dataChartProps?.optionId?.option_id || "",
      type: state.activeTab,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  // Reset isTabSwitching when query completes
  useEffect(() => {
    if (!isLoadingPerformanceDetailsData && !isFetchingPerformanceDetailsData) {
      setState((prev) => ({ ...prev, isTabSwitching: false }));
    }
  }, [isLoadingPerformanceDetailsData, isFetchingPerformanceDetailsData]);

  useEffect(() => {
    if (performanceDetailsData?.data?.option) {
      setLastValidOption(performanceDetailsData.data.option);
    }
  }, [performanceDetailsData?.data?.option]);

  // Memoize tabMap (static)
  const tabMap = useMemo(
    () => ({
      0: "profile",
      1: "retention",
      2: "acquisition",
    }),
    []
  );

  // Memoize tabs (depends on translation function t)
  const tabs = useMemo(
    () => [
      { label: t("performan.performanceAgent.profile"), value: "profile" },
      { label: t("performan.performanceAgent.retention"), value: "retention" },
      {
        label: t("performan.performanceAgent.acquisition"),
        value: "acquisition",
      },
    ],
    [t]
  );

  // Set active tab based on navigation state, defaulting to "profile"
  useEffect(() => {
    const incomingType = location.state?.dataChartProps?.type;
    if (incomingType) {
      const validTab =
        typeof incomingType === "string" &&
        tabs.some((tab) => tab.value === incomingType)
          ? incomingType
          : tabMap[incomingType] || "profile";
      setState((prev) => ({
        ...prev,
        activeTab: validTab,
        isTabSwitching: true,
      }));
    }
  }, [location.state?.dataChartProps?.type, tabMap, tabs]);

  // Log performanceDetailsData to debug data structure
  useEffect(() => {}, [performanceDetailsData, state.activeTab]);

  const chartData = useMemo(
    () => ({
      data: dataChartProps?.chartData?.data || [],
      labels: dataChartProps?.chartData?.labels || [],
      options: dataChartProps?.chartData?.options || [],
    }),
    [dataChartProps]
  );

  const hasData = chartData.data.some((value) => value > 0);

  const colors = useMemo(() => {
    if (!chartData.labels.length) {
      return chartData.labels.map(() => "#EEEAE8");
    }

    const optionToUse = dataChartProps?.optionId?.option_id || lastValidOption;

    if (!optionToUse) {
      return chartData.labels.map(() => "#EEEAE8");
    }

    return chartData.labels.map((_, index) => {
      const isMatchingOption =
        chartData.options[index]?.option_id === optionToUse;
      return isMatchingOption ? "#62B2FD" : "#EEEAE8";
    });
  }, [
    chartData.labels,
    chartData.options,
    dataChartProps?.optionId,
    lastValidOption,
  ]);

  // Define data for retention and acquisition tabs
  const RETENTION_DATA = Array.isArray(performanceDetailsData?.data)
    ? performanceDetailsData.data
    : performanceDetailsData?.data?.metrics ||
      performanceDetailsData?.data?.data ||
      performanceDetailsData?.data?.statistics ||
      [];
  const ACQUISITION_DATA = Array.isArray(performanceDetailsData?.data)
    ? performanceDetailsData.data
    : performanceDetailsData?.data?.metrics ||
      performanceDetailsData?.data?.data ||
      performanceDetailsData?.data?.statistics ||
      [];

  const handleSegmentClick = (clickedData) => {
    const { index } = clickedData || {};

    if (index !== undefined && chartData.data[index] !== undefined) {
      const clickedOption = chartData.options[index] || {};
      const segmentationId = dataChartProps?.segmentId;
      const clickedType = state.activeTab;

      // Set isTabSwitching to trigger loading during API fetch
      setState((prev) => ({ ...prev, isTabSwitching: true }));

      navigate(`/performan/agent`, {
        state: {
          dataChartProps: {
            segmentId: segmentationId,
            locationId: dataChartProps?.locationId,
            optionId: clickedOption,
            type: clickedType,
            chartData: chartData,
          },
        },
      });
    }
  };

  return (
    <>
      <main>
        <div className="sticky top-0 left-0 w-full z-50">
          <LoadingCommon
            statusLoading={
              isLoadingPerformanceDetailsData ||
              isFetchingPerformanceDetailsData ||
              state.isTabSwitching
            }
          />
          <Header
            backIcon={images.BackIcon}
            onBackClick={() => navigate("/performan")}
            title={t("performan.performanceAgent.performance")}
          />
        </div>
        <div className="p-3 bg-white rounded-xl w-full max-sm:p-2 max-sm:w-full mx-auto">
          <div className="w-full mt-3">
            {hasData ? (
              <CommonPieChart
                data={chartData.data}
                labels={chartData.labels}
                colors={colors}
                outerRadius={120}
                showLabel={true}
                width="100%"
                cy="50%"
                onSegmentClick={(clickedData) =>
                  handleSegmentClick(clickedData)
                }
              />
            ) : (
              <div className="flex justify-center items-center h-full">
                <p className="text-zinc-400">
                  {t("performan.performanceAgent.noDataAvailable")}
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="p-3 bg-white rounded-xl w-[396px] max-sm:p-2 max-sm:w-full mx-auto">
          <div className="flex flex-1 justify-center gap-3 items-center self-center py-5 max-w-full w-full h-full text-xs font-medium whitespace-nowrap text-zinc-500">
            {tabs.map((tab) => (
              <div
                key={tab.value}
                className={`flex overflow-hidden gap-1.5 justify-center items-center self-stretch py-2 pr-2.5 pl-3 my-auto rounded-xl min-h-[30px] cursor-pointer ${
                  state.activeTab === tab.value
                    ? "bg-[#3F54DF] text-white"
                    : "bg-stone-200"
                }`}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    activeTab: tab.value,
                    isTabSwitching: true,
                  }))
                }
              >
                <div className="gap-1.5 self-stretch my-auto">{tab.label}</div>
              </div>
            ))}
          </div>

          {state.activeTab === "profile" && (
            <Profile
              performanceDetailsDataProfile={performanceDetailsData?.data || {}}
            />
          )}

          {state.activeTab === "retention" && (
            <div>
              {RETENTION_DATA.length > 0 ? (
                RETENTION_DATA.map((metric, index) => (
                  <MetricCardRetention
                    key={metric.metric || index}
                    label={metric.metric || ""}
                    value={metric.value || 0}
                    opacity={100 - index * 5}
                    classNameLabel={"text-sm"}
                    classNameValue={`min-w-[50px] font-normal`}
                    status={metric.status}
                    unit={metric.unit}
                    unit_position={metric.unit_position}
                  />
                ))
              ) : (
                <p className="text-zinc-400 text-center">
                  {t("performan.performanceAgent.noRetentionData")}
                </p>
              )}
            </div>
          )}

          {state.activeTab === "acquisition" && (
            <div>
              {ACQUISITION_DATA.length > 0 ? (
                ACQUISITION_DATA.map((metric, index) => (
                  <MetricCardRetention
                    key={metric.metric || index}
                    label={metric.metric || ""}
                    value={metric.value || 0}
                    opacity={100 - index * 5}
                    classNameLabel={"text-sm"}
                    classNameValue={`min-w-[50px] font-normal`}
                    status={metric.status}
                    unit={metric.unit}
                    unit_position={metric.unit_position}
                  />
                ))
              ) : (
                <p className="text-zinc-400 text-center">
                  {t("performan.performanceAgent.noAcquisitionData")}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

export default PerformaceAgent;
